import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetOnboardingStatus } from '@/hooks/use-company';
import { useGetArchitectOnboardingStatus, useGetSubcontractorOnboardingStatus } from '@/hooks/use-contractors';

interface OnboardingStatusContextType {
    // Status data
    onboardingStatus: any | null;
    isLoading: boolean;

    // Computed values
    completedStepsFromAPI: Set<number>;
    optionalStepsFromAPI: Set<number>;
    overallProgress: number;

    // Real-time state management
    internalCompletedSteps: Set<number>;
    markStepCompleted: (step: number) => void;
    isStepCompleted: (step: number) => boolean;
    isStepOptional: (step: number) => boolean;

    // Account type and organization
    accountType: 'company' | 'architect' | 'subcontractor';
    organizationId: string | null;

    // Progress calculation
    getTotalSteps: () => number;
    getProgressPercentage: () => number;
}

const OnboardingStatusContext = createContext<OnboardingStatusContextType | undefined>(undefined);

interface OnboardingStatusProviderProps {
    children: ReactNode;
    accountType?: 'company' | 'architect' | 'subcontractor';
}

export function OnboardingStatusProvider({ children, accountType: propAccountType }: OnboardingStatusProviderProps) {
    const [searchParams] = useSearchParams();
    const organizationId = searchParams.get('orgId');
    const typeFromParams = searchParams.get('type') as 'company' | 'architect' | 'subcontractor' | null;

    // Determine account type from props or URL params
    const accountType = typeFromParams || propAccountType || 'company';

    // Fetch onboarding status based on account type - SINGLE SOURCE OF TRUTH
    const { data: companyStatus, isLoading: isLoadingCompany } = useGetOnboardingStatus(
        accountType === 'company' ? organizationId : null
    );
    const { data: architectStatus, isLoading: isLoadingArchitect } = useGetArchitectOnboardingStatus(
        accountType === 'architect' ? organizationId : null
    );
    const { data: subcontractorStatus, isLoading: isLoadingSubcontractor } = useGetSubcontractorOnboardingStatus(
        accountType === 'subcontractor' ? organizationId : null
    );

    // Get the appropriate status and loading state
    const onboardingStatus = accountType === 'architect'
        ? architectStatus
        : accountType === 'subcontractor'
            ? subcontractorStatus
            : companyStatus;

    const isLoading = accountType === 'architect'
        ? isLoadingArchitect
        : accountType === 'subcontractor'
            ? isLoadingSubcontractor
            : isLoadingCompany;

    // Real-time completed steps state for immediate UI feedback
    const [internalCompletedSteps, setInternalCompletedSteps] = useState<Set<number>>(new Set());

    // Define which steps are optional for each account type
    const optionalStepsConfig = React.useMemo(() => {
        if (accountType === 'architect' || accountType === 'subcontractor') {
            return new Set([4, 6]); // Documents (step 4) and Team Members (step 6) are optional
        }
        return new Set<number>(); // No optional steps for company
    }, [accountType]);

    // Computed completed steps from API - IGNORE optional steps completion from backend
    const completedStepsFromAPI = React.useMemo(() => {
        const completedSet = new Set<number>();

        if (onboardingStatus) {
            if (accountType === 'company') {
                // Company step mapping
                const stepKeys = ['profile', 'teamMembers', 'configuration', 'elements', 'complete'];
                stepKeys.forEach((key, index) => {
                    if (onboardingStatus[key as keyof typeof onboardingStatus]) {
                        completedSet.add(index + 1);
                    }
                });
            } else {
                // Architect/Subcontractor step mapping - ignore backend completion for optional steps
                const stepKeys = ['businessInfo', 'classification', 'contactInfo', 'documents', 'agreements', 'teamMembers'];
                stepKeys.forEach((key, index) => {
                    const stepNumber = index + 1;
                    if (onboardingStatus[key as keyof typeof onboardingStatus]) {
                        // If this step is optional, ignore backend completion - let local navigation handle it
                        if (optionalStepsConfig.has(stepNumber)) {
                            // Don't add to completed set - will be handled locally when navigated
                            return;
                        } else {
                            // Required step that's completed
                            completedSet.add(stepNumber);
                        }
                    }
                });
            }
        }

        return completedSet;
    }, [onboardingStatus, accountType, optionalStepsConfig]);

    // Optional steps are empty now - handled locally
    const optionalStepsFromAPI = React.useMemo(() => {
        return new Set<number>();
    }, []);

    // Initialize internal completed steps from API when data loads
    useEffect(() => {
        if (completedStepsFromAPI.size > 0) {
            setInternalCompletedSteps(prev => {
                const newSet = new Set([...prev]);
                completedStepsFromAPI.forEach(step => newSet.add(step));
                return newSet;
            });
        }
    }, [completedStepsFromAPI]);

    // Get total steps based on account type
    const getTotalSteps = React.useCallback(() => {
        return accountType === 'company' ? 5 : 6;
    }, [accountType]);

    // Calculate overall progress - SINGLE SOURCE OF TRUTH
    const overallProgress = React.useMemo(() => {
        // Always calculate based on combined local + API state for real-time updates
        const totalSteps = getTotalSteps();

        // Combine completed steps from API and local state
        const allCompletedSteps = new Set([...completedStepsFromAPI, ...internalCompletedSteps]);
        const completedCount = allCompletedSteps.size;

        const calculatedProgress = Math.min((completedCount / totalSteps) * 100, 100);

        // Use calculated progress since we're managing optional steps locally
        return calculatedProgress;
    }, [completedStepsFromAPI, internalCompletedSteps, getTotalSteps]);

    // Get progress percentage (same as overallProgress for convenience)
    const getProgressPercentage = React.useCallback(() => {
        return overallProgress;
    }, [overallProgress]);

    // Mark step as completed - IMMEDIATE UI UPDATE
    const markStepCompleted = React.useCallback((step: number) => {
        setInternalCompletedSteps(prev => {
            if (!prev.has(step)) {
                const newSet = new Set([...prev, step]);
                return newSet;
            }
            return prev;
        });
    }, []);

    // Check if step is completed - COMBINED STATE CHECK
    const isStepCompleted = React.useCallback((step: number) => {
        return internalCompletedSteps.has(step) || completedStepsFromAPI.has(step);
    }, [internalCompletedSteps, completedStepsFromAPI]);

    // Check if step is optional - based on config, not API
    const isStepOptional = React.useCallback((step: number) => {
        return optionalStepsConfig.has(step);
    }, [optionalStepsConfig]);

    const contextValue: OnboardingStatusContextType = {
        onboardingStatus,
        isLoading,
        completedStepsFromAPI,
        optionalStepsFromAPI,
        overallProgress,
        internalCompletedSteps,
        markStepCompleted,
        isStepCompleted,
        isStepOptional,
        accountType,
        organizationId,
        getTotalSteps,
        getProgressPercentage,
    };

    return (
        <OnboardingStatusContext.Provider value={contextValue}>
            {children}
        </OnboardingStatusContext.Provider>
    );
}

export function useOnboardingStatus() {
    const context = useContext(OnboardingStatusContext);
    if (context === undefined) {
        throw new Error('useOnboardingStatus must be used within an OnboardingStatusProvider');
    }
    return context;
}
